<template>
  <article
    v-if="article && article.isVisible"
    class="article-card"
    :class="articleClass"
  >

    <NuxtLink
      :to="getDetailPath()"
      :target="getDetailTarget()"
      class="article-card__aside"
    >
      <DefaultArticlesArticleImage
        :src="props.article?.previewAsset?.urlWithProtocol || props.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.urlWithProtocol"
        :alt="props.article?.title"
        :sizes="imageSizes"
      />
    </NuxtLink>

    <div class="article-card__body">

      <component :is="headingElement">
        <NuxtLink
          :to="getDetailPath()"
          :target="getDetailTarget()"
          v-html="props.article?.title"
        >
        </NuxtLink>
      </component>

      <div class="article-card__foot">
        <DefaultArticlesAuthorAndDateInfo
          v-if="!props.hideAuthor"
          :author="getSimpleAuthor()"
          :published="props.article?.publishedDate"
        />
        <DefaultArticlesPodcastRemaining v-if="isPodcast()" :remaining="5"/>

        <!--read-time and publisher-->
        <div v-if="getReadingTime()" class="font-bold uppercase">{{ getReadingTime() }} min</div>
        <span v-if="getReadingTime()" class="w-2 h-px"></span>
        <DefaultArticlesArticleTag
          v-if="!props.hideMagazine && getTagName()"
          :text="getTagName()"
        />
      </div>
    </div>

    <!-- FOR ARTICLE DEBUGGING -->
    <div
      v-if="debugCookie"
      class="w-8 h-8 absolute right-2 top-2 bg-black opacity-75 rounded-full cursor-pointer"
      @click="isDebugModalOpen = !isDebugModalOpen"
    >
      <UIcon name="i-heroicons-light-bulb" class="w-4 h-4 fill-white opacity-100 text-white mt-2 ml-2" />
    </div>

    <UModal v-model="isDebugModalOpen">
      <div class="p-4 text-black">
        <strong>Published:</strong><br />
        <span>{{ props.article?.publishedDate }}</span>
        <br /><br />

        <strong>ID (enhanced ULID):</strong><br />
        <span>{{ props.article?.id }}</span>
        <br /><br />

        <strong>UUID:</strong><br />
        <span>{{ apiIdToUuid(props.article?.id) }}</span>
        <br /><br />

        <strong class="pb-1 mt-3">AI Topics:</strong><br />
        <ul class="mt-0">
          <li v-for="c in props.article?.aiTopicList?.edges" class="m-0 pb-0">
            {{ c?.node?.name }}: {{ c?.node?.id }}
          </li>
        </ul>
        <br />

        <strong class="pb-1 mt-3">Categories:</strong><br />
        <ul class="mt-0">
          <li v-for="c in props.article?.categoryList?.edges" class="m-0 pb-0">
            {{ c?.node?.name }}: {{ c?.node?.id }}
          </li>
        </ul>
      </div>
    </UModal>
    <!-- /FOR ARTICLE DEBUGGING -->

  </article>
</template>

<script setup lang="ts">
import type ArticleLocal from "~/libs/interfaces/ArticleLocal"
import type {PropType} from "vue"
import {DEBUG_COOKIE} from "~/libs/constants/constants"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import type SimpleAuthor from "~/libs/interfaces/SimpleAuthor"
import {useArticleTools} from "~/composables/useArticleTools"
import {useApiIdConverter} from "~/composables/useApiIdConverter";

const props = defineProps({
  article: {type: Object as PropType<ArticleLocal>, required: false},
  trending: {type: Boolean, required: false, default: false},
  isReader: {type: Boolean, required: false, default: false},
  shouldExpand: {type: Boolean, required: false},
  prevPage: {type: String, required: false},
  headingElement: {type: String, required: false, default: 'h2'},
  type: { type: String as PropType<ArticleComponentType>, required: false, default: ArticleComponentType.DEFAULT },
  hideAuthor: {type: Boolean, required: false, default: false},
  hideMagazine: {type: Boolean, required: false, default: false},
  showMagazineName: {type: Boolean, required: false, default: false},
})

const debugCookie = useCookie(DEBUG_COOKIE)
const localePath = useLocalePath()
const apiIdToUuid = useApiIdConverter().apiIdToUuid

const isDebugModalOpen = ref(false)

const imageSizes = computed(() => {
  switch (props.type) {
    case ArticleComponentType.MAIN:
    case ArticleComponentType.BIG:
      return 'xs:100vw lg:680px'
    case ArticleComponentType.SIDE:
      return 'xs:100vw sm:50vw lg:246px'    // xs:flex-col, sm:flex-row (~50%), lg:flex-col (75% of 328px)
    case ArticleComponentType.SEARCH:
      return 'xs:100vw sm:58vw lg:400px'    // xs:flex-col (full-width), sm:w-7/12
    case ArticleComponentType.RECOMMENDED:
      return 'xs:58vw lg:400px'             // always flex-row w-7/12
    default:
      return 'xs:100vw sm:50vw lg:330px'    // xs:flex-col, sm:flex-row (~50%), lg: aside ~330px
  }
})

const articleClass = computed(() => {
  switch (props.type) {
    case ArticleComponentType.BIG:
      return 'article-card--big'
    case ArticleComponentType.MAIN:
      return 'article-card--big article-card--overlay'
    case ArticleComponentType.SEARCH:
      return 'article-card--search'
    case ArticleComponentType.SIDE:
      return 'article-card--side'
    case ArticleComponentType.RECOMMENDED:
      return 'article-card--recommended'
    case ArticleComponentType.DEFAULT:
    default:
      return ''
  }
})

const getLinkParams = (): string => {
  return props.prevPage ? `?prevPage=${props.prevPage}` : ''
}

const getLinkQuery = () => {
  return props.prevPage ? {
    prevPage: props.prevPage
  } : {}
}

const getTagName = () => {
  if (props.showMagazineName) {
    return props.article?.magazineName ? String(props.article?.magazineName) : undefined
  }
  return props.article?.categoryList?.edges?.at(0)?.node?.name ? String(props.article?.categoryList?.edges?.at(0)?.node?.name) : undefined
}

const getDetailPath = () => {
  return useArticleTools(props.article).getDetailPath()
}

const getDetailTarget = () => {
  if (props.article?.path) {
    return
  } else if (props.article?.url) {
    return '_blank'
  }
}

const isPodcast = (): boolean => {
  return false
}

const getSimpleAuthor = (): SimpleAuthor | undefined => {
  if (props.article?.simpleAuthor) {
    return props.article?.simpleAuthor
  }
  if (props.article?.authorsString) {
    return {
      name: props.article?.authorsString
    }
  }
}

const getReadingTime = (): Number | undefined => {
  return props.article?.readingTime ? Number(props.article?.readingTime) : undefined
}
</script>
